/**
 * Locate the nearest scrollable ancestor of `start`. Used by floating chrome
 * (back-to-top, scroll-restoration) to attach scroll listeners to the actual
 * scroll surface — which in this app is `<SidebarInset>` (overflow-y-auto +
 * h-dvh, see `components/ui/sidebar.tsx`), NOT `window`.
 *
 * Returns `document.scrollingElement` as a final fallback so the caller can
 * always use the same `Element`-based API (`scrollTop`, `scrollTo`, scroll
 * events) regardless of layout. Returns `null` only in non-browser contexts.
 */
export function getScrollContainer(start: Element | null): HTMLElement | null {
  if (typeof window === "undefined") return null;

  // Fast path: in this app, the scroll surface is always the shadcn
  // <SidebarInset> — tagged with `data-sidebar="inset"`. Look it up
  // directly when available so callers don't need to be mounted in the
  // DOM at all (works even when `start` is null).
  const inset = document.querySelector<HTMLElement>('[data-sidebar="inset"]');
  if (inset) return inset;

  // Fallback: walk up from the caller's anchor looking for any scrollable
  // ancestor — covers stations / station-mode layouts that don't render
  // SidebarInset.
  let el: Element | null = start?.parentElement ?? null;
  while (el && el !== document.body && el !== document.documentElement) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const overflow = style.overflow;
    const isScrollable =
      overflowY === "auto" || overflowY === "scroll" || overflow === "auto" || overflow === "scroll";
    if (isScrollable && el.scrollHeight > el.clientHeight) {
      return el as HTMLElement;
    }
    el = el.parentElement;
  }

  // Final fallback for non-sidebar layouts (e.g. plain document scroll).
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/**
 * Smoothly scroll the given container to the top, falling back to `auto` for
 * users with `prefers-reduced-motion`.
 */
export function scrollContainerToTop(container: HTMLElement | null) {
  if (!container || typeof container.scrollTo !== "function") return;
  container.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

/** True when the user asked for reduced motion (false outside a browser). */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

/**
 * The scroll margin that lands an element directly under the sticky chrome:
 * the TopHeader plus the measured PageActionBar (`--page-action-bar-height`,
 * DESIGN.md section 14.2, which wraps taller on narrow viewports) plus 1rem of
 * air. Use this class on anything `scrollIntoViewBelowActionBar` targets so a
 * native anchor jump lands in the same place.
 */
export const BELOW_ACTION_BAR_SCROLL_MARGIN =
  "scroll-mt-[calc(var(--top-header-height,3.5rem)+var(--page-action-bar-height,0px)+1rem)]";

const BELOW_ACTION_BAR_MARGIN_VALUE =
  "calc(var(--top-header-height, 3.5rem) + var(--page-action-bar-height, 0px) + 1rem)";

/**
 * True when a box between `el` and `surface` can scroll up and down itself.
 * The 1px slack: Windows display scaling (125%, 150%) can report a box 1px
 * taller than it shows, which would send a sideways-only box down the native
 * path that parks the row under the bar.
 */
function hasVerticalScrollerBetween(el: Element, surface: Element): boolean {
  for (let node = el.parentElement; node && node !== surface; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight + 1) return true;
  }
  return false;
}

/**
 * Scroll `el` to the top of the app's scroll surface, just below the sticky
 * TopHeader and PageActionBar, never under them. The margin comes from the
 * same CSS variables the sticky surfaces publish, so there is no hard-coded
 * pixel offset to drift. Smooth, or instant under reduced motion. A missing
 * element is a no-op.
 *
 * A sideways-scroll box between the element and the app surface (a wide
 * table's `overflow-x-auto` wrapper) is a scroll container on both axes, and
 * Chrome clips the element's scroll margin to that box's top, so a native
 * `scrollIntoView` parks the box under the bar. When nothing in between can
 * scroll up and down, the surface is scrolled by hand to the same spot; an
 * element inside a box that does scroll vertically keeps the native call,
 * which scrolls that box too. The manual path never scrolls sideways, so a
 * caller targeting a narrow cell inside a sideways-scroll box scrolls that
 * cell into view across the box itself.
 */
export function scrollIntoViewBelowActionBar(el: Element | null | undefined): void {
  if (!el || typeof (el as HTMLElement).scrollIntoView !== "function") return;
  const target = el as HTMLElement;
  // Set inline too, so a caller that forgot the class still clears the bar.
  if (target.style && !target.style.scrollMarginTop) target.style.scrollMarginTop = BELOW_ACTION_BAR_MARGIN_VALUE;
  const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
  const surface = document.querySelector<HTMLElement>('[data-sidebar="inset"]');
  if (
    surface &&
    surface !== target &&
    surface.contains(target) &&
    typeof surface.scrollTo === "function" &&
    !hasVerticalScrollerBetween(target, surface)
  ) {
    const margin = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    const top = target.getBoundingClientRect().top - surface.getBoundingClientRect().top + surface.scrollTop - margin;
    surface.scrollTo({ top: Math.max(0, top), behavior });
    return;
  }
  target.scrollIntoView({ block: "start", behavior });
}
